import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import {DocumentNode} from 'graphql';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as DateUtil from 'util/DateUtil';

import {ProcedureRunStateSummary} from '../shared/MultiProcedureRunOverviewTabContents';
import {Cell, Header, HeaderCell, Row, Table} from '../StudyTableComponent';
import {StudyInaccessibleContentBanner, StudyQueryErrorBanner} from '../utils/ui/StudyQueryBanners';
import {
  MultiProcedureRunListingTableQuery,
  MultiProcedureRunListingTableQueryVariables,
  MultiProcedureRunListingTableQuery_study_StudyNode_procedureRuns as ProcedureRunNode,
  MultiProcedureRunListingTableQuery_study_StudyNode_procedureRuns_ProcedureRunNode as AccessibleProcedureRunNode,
} from './__generated/MultiProcedureRunListingTableQuery';
import NoExperimentOverview from './NoExperimentOverview';

export const INACCESSIBLE_PROCEDURE_RUNS_BANNER_TEXT =
  'One or more recipe run(s) are hidden. Request access from your admin to view.';

export const MULTI_PROCEDURE_RUN_LISTING_TABLE_QUERY: DocumentNode = gql`
  query MultiProcedureRunListingTableQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
        procedureRuns {
          ... on ProcedureRunNode {
            id
            name
            createdAt
            templateProcedureFlowchartVersion {
              ... on ProcedureFlowchartVersionNode {
                id
                procedureFlowchart {
                  ... on ProcedureFlowchartNode {
                    id
                    parent {
                      id
                    }
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ProcedureRunOverview {
  id: string;
  name: string;
  procedureTemplateId: string;
  createdAt: string;
}

interface ProcedureRunOverviewsAndInfo {
  procedureRunOverviews: Array<ProcedureRunOverview>;
  hasInaccessibleProcedureRuns: boolean;
}

function getProcedureRunOverviews(procedureRuns: Array<ProcedureRunNode>): ProcedureRunOverviewsAndInfo {
  const procedureRunOverviews: Array<ProcedureRunOverview> = [];
  const accessibleProcedureRuns = procedureRuns.filter(
    (procedureRun) =>
      !isInaccessible(procedureRun) && !isInaccessible(procedureRun.templateProcedureFlowchartVersion)
  ) as Array<AccessibleProcedureRunNode>;
  for (const procedureRun of accessibleProcedureRuns) {
    // asserting for typing purposes
    assert(
      !isInaccessible(procedureRun.templateProcedureFlowchartVersion),
      'Expected template procedure flowchart version to be accessible.'
    );
    // Procedure flowchart parent is null when the type is procedure run, but we expect it to be of type
    // procedure here as the procedure holds the template
    assert(
      procedureRun.templateProcedureFlowchartVersion.procedureFlowchart?.parent != null,
      'Expected procedure flowchart parent to be non-null.'
    );
    procedureRunOverviews.push({
      ...procedureRun,
      procedureTemplateId: procedureRun.templateProcedureFlowchartVersion.procedureFlowchart.parent.id,
    });
  }
  return {
    procedureRunOverviews,
    hasInaccessibleProcedureRuns: procedureRuns.length > accessibleProcedureRuns.length,
  };
}
interface MultiProcedureRunParamsTableProps {
  studyId: string;
  setSelectedProcedureRun: (procedureRunStateSummary: ProcedureRunStateSummary) => void;
}

export default function MultiProcedureRunListingTable({
  studyId,
  setSelectedProcedureRun,
}: MultiProcedureRunParamsTableProps): JSX.Element {
  const result = useBenchlingQuery<
    MultiProcedureRunListingTableQuery,
    MultiProcedureRunListingTableQueryVariables
  >(MULTI_PROCEDURE_RUN_LISTING_TABLE_QUERY, {variables: {studyId}});

  if (result.type === QueryResultType.PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <CenteredHorizontalThrobber />
      </>
    );
  } else if (result.type === QueryResultType.ERROR) {
    return <StudyQueryErrorBanner />;
  } else {
    const {data} = result;
    if (isInaccessible(data.study)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study}
          modelType={ModelTypeConstants.STUDY}
          displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
        />
      );
    }
    const {procedureRunOverviews, hasInaccessibleProcedureRuns} = getProcedureRunOverviews(
      data.study.procedureRuns
    );
    return (
      <>
        {hasInaccessibleProcedureRuns && (
          <div className={css(styles.inaccessibleProcedureRunBanner)}>
            <InlineBanner bannerType={InlineBannerType.INFO} text={INACCESSIBLE_PROCEDURE_RUNS_BANNER_TEXT} />
          </div>
        )}
        <RenderMultiProcedureRunListingTable
          studyId={studyId}
          studyType={data.study.studySchema.studyType}
          procedureRuns={procedureRunOverviews}
          setSelectedProcedureRun={setSelectedProcedureRun}
        />
      </>
    );
  }
}

interface RenderMultiProcedureRunParamsTableProps {
  studyId: string;
  studyType: StudyType;
  procedureRuns: Array<ProcedureRunOverview>;
  setSelectedProcedureRun: (procedureRunStateSummary: ProcedureRunStateSummary) => void;
}

export function RenderMultiProcedureRunListingTable({
  studyId,
  studyType,
  procedureRuns,
  setSelectedProcedureRun,
}: RenderMultiProcedureRunParamsTableProps): JSX.Element {
  return procedureRuns.length === 0 ? (
    <div className={css(styles.noExperimentViewStyle)}>
      <NoExperimentOverview studyId={studyId} studyType={studyType} />
    </div>
  ) : (
    <div className={css(styles.procedureRunListing)}>
      <Table dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'table'}}>
        <Header className={css(styles.headerRowStyle)}>
          <HeaderCell className={css(styles.headerNameCell)}> Name </HeaderCell>
          <HeaderCell className={css(styles.headerCreatedAtCell)}> Created </HeaderCell>
          <HeaderCell className={css(styles.headerRecipeChipCell)}> Recipe </HeaderCell>
        </Header>
        {procedureRuns.map((procedureRun) => (
          <Row
            key={procedureRun.id}
            onClick={() => {
              setSelectedProcedureRun({id: procedureRun.id, name: procedureRun.name});
            }}
            className={css(styles.rowStyle)}
            dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'row'}}
          >
            <Cell className={css(styles.nameCell)}>{procedureRun.name}</Cell>
            <Cell className={css(styles.headerCreatedAtCell)}>
              <div className={css(styles.createdAtCell)}>
                {DateUtil.readableTime(procedureRun.createdAt, {showTime: true})}
              </div>
            </Cell>
            <Cell className={css(styles.recipeChipCell)}>
              <BenchChip id={procedureRun.procedureTemplateId} size={ChipSize.MINI} />
            </Cell>
          </Row>
        ))}
      </Table>
    </div>
  );
}

const styles = StyleSheet.create({
  createdAtCell: {
    flex: '0 0 15%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
    display: 'flex',
    alignItems: 'center',
  },
  nameCell: {
    flex: '0 0 15%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  recipeChipCell: {
    flex: '0 0 30%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
  },
  loadingDiv: {
    paddingBottom: 5,
  },
  rowStyle: {
    width: '100%',
    minHeight: 44,
  },
  headerRowStyle: {
    height: 32,
    alignContent: 'center',
    color: Colors.GRAY_SEMIDARK,
    ...FONT_PROXIMA_13,
  },
  headerCreatedAtCell: {
    flex: '0 0 15%',
  },
  headerNameCell: {
    flex: '0 0 15%',
  },
  headerRecipeChipCell: {
    flex: '0 0 30%',
  },
  procedureRunListing: {
    backgroundColor: Color.WHITE,
  },
  noExperimentViewStyle: {
    height: '100%',
  },
  inaccessibleProcedureRunBanner: {
    padding: 4,
    backgroundColor: Color.WHITE,
  },
});
